import { Injectable, type Signal, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** The locales Nabotavle ships (Phase V1.12). */
export const SUPPORTED_LOCALES = ['nb', 'nn', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'nb.locale';

/** The Angular/Intl locale tag for a Nabotavle locale (DatePipe, number formatting). */
export function toAngularLocale(locale: Locale | string | null | undefined): 'nb' | 'nn' | 'en-GB' {
  switch (locale) {
    case 'nn':
      return 'nn';
    case 'en':
      return 'en-GB';
    default:
      return 'nb';
  }
}

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Suffix for the ngx-translate HTTP loader. `/i18n/<lang>.json` is not content-hashed, so a browser can keep an
 * old copy and pair it with a new bundle (raw keys like `reset.forgotLink` after a deploy). Keying the URL to the
 * build makes every deploy fetch fresh files, whatever the client's cache holds.
 * ponytail: the build stamp is index.html's Last-Modified (set by `ng build`, served by nginx, kept by the SW);
 * swap for a real build id if index.html ever stops carrying one.
 */
export const I18N_SUFFIX = `.json?v=${typeof document === 'undefined' ? '' : document.lastModified.replace(/\D/g, '')}`;

/**
 * The **bootstrap** locale — it runs before any HTTP, so only the client-side part of the precedence
 * applies: persisted user choice (localStorage) > browser language > <c>nb</c>. The server-side part
 * (profile choice / tenant default) arrives later through
 * <see cref="LocaleService.applyServerPreference"/>. Pure — safe to call at bootstrap before DI.
 */
export function resolveInitialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) {
      return saved;
    }
  } catch {
    /* localStorage unavailable (native/SSR) — fall through */
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2).toLowerCase() : undefined;
  return isSupported(browser) ? browser : 'nb';
}

/**
 * Thin wrapper over ngx-translate's <see cref="TranslateService"/> (v18) — exposes the supported set, the
 * reactive current locale (a signal, so it works zoneless/OnPush), and a persisting <c>use()</c>. The
 * loader + fallback are configured in each app's <c>provideTranslateService</c>. Deliberately
 * **HTTP-free**: the apps own persistence (kundeapp/styreapp PUT the choice to the profile).
 *
 * **Full precedence** (highest first):
 * <list type="number">
 *   <item>the user's choice on their **profile** (server, cross-device) — <c>applyServerPreference</c>;</item>
 *   <item>the user's **local** choice on this device (<c>nb.locale</c> in localStorage) — <c>use()</c>,
 *         also what <c>resolveInitialLocale()</c> reads at bootstrap;</item>
 *   <item>the **lag's default language** (<c>defaultLocale</c> from min-konfigurasjon) — applied only
 *         when the user has made no choice at all, and never recorded as their own;</item>
 *   <item>the **browser** language;</item>
 *   <item><c>nb</c>.</item>
 * </list>
 * Steps 2, 4 and 5 run synchronously at bootstrap (<c>resolveInitialLocale</c>); steps 1 and 3 need the
 * API and are applied by each app's shell once <c>/api/profil/me</c> + <c>/api/min-konfigurasjon</c> resolve.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly translate = inject(TranslateService);

  readonly supported = SUPPORTED_LOCALES;
  /** The active locale (reactive). Null only before the first language is applied. */
  readonly current: Signal<string | null> = this.translate.currentLang;

  /** Register the supported locales (call once at startup, e.g. from an app initializer). */
  init(): void {
    this.translate.addLangs([...SUPPORTED_LOCALES]);
  }

  /** The Intl/Angular locale tag for the active locale (for components that format dates themselves). */
  readonly intlLocale = computed(() => toAngularLocale(this.current()));

  /** Switch + persist the user's locale (runtime — no rebuild). Records an explicit choice. */
  use(locale: Locale): void {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* persistence best-effort */
    }
    this.appliedTenantDefault = null;
    this.translate.use(locale);
  }

  /**
   * Whether the user has picked a language **on this device** (i.e. `nb.locale` is set). A tenant default
   * applied through {@link applyServerPreference} does not count — it is not the user's own choice.
   */
  hasExplicitChoice(): boolean {
    try {
      return isSupported(localStorage.getItem(STORAGE_KEY));
    } catch {
      return false; /* localStorage unavailable (native/SSR) */
    }
  }

  /**
   * Applies the server-side part of the precedence once the profile + tenant config have loaded:
   * a supported <paramref name="profileLocale"/> wins over everything (and is mirrored into localStorage so
   * the next cold start is instant); otherwise a supported <paramref name="tenantDefault"/> applies only
   * when the user has no explicit local choice — and is **not** recorded as one, so a later profile choice
   * still wins and {@link hasExplicitChoice} stays false. Unsupported/absent values are ignored.
   */
  applyServerPreference(profileLocale: string | null | undefined, tenantDefault?: string | null): void {
    if (isSupported(profileLocale)) {
      this.use(profileLocale);
      return;
    }
    if (!this.hasExplicitChoice() && isSupported(tenantDefault)) {
      this.applyTenantDefault(tenantDefault);
    }
  }

  /** The lag default currently applied (never persisted) — so re-running the shell effect is a no-op. */
  private appliedTenantDefault: Locale | null = null;

  private applyTenantDefault(locale: Locale): void {
    if (this.appliedTenantDefault === locale) return;
    this.appliedTenantDefault = locale;
    this.translate.use(locale); // deliberately not persisted: the lag's default, not the user's choice
  }
}
